import { ArrowDown, ArrowRightLeft, ArrowUp, Card, CardContent, CardHeader, CardTitle, Label } from '@chain-zen/ui'

import type { IDateTime, TStopType } from '../../../../../types/post'
import type { IStop, IStopWarehouse } from '../../../../../types/shipment'
import { DateTimeField } from '../../date-time-field'
import { AppointmentSection } from './appointment-section'
import { useStops } from './contexts/use-stops-context'
import { AppointmentDisplay } from './display/appointment-display'
import { InstructionsDisplay } from './display/instructions-display'
import { RefNumberDisplay } from './display/ref-number-display'
import { WarehouseDisplay } from './display/warehouse-display'
import { InstructionsInput } from './editable/instructions-input'
import { RefNumberInput } from './editable/ref-number-input'
import { StopTypeInput } from './editable/stop-type-input'
import { WarehouseInput } from './editable/warehouse-input'
import { useUpdateStopDetails } from './hooks/use-update-stop-details'
import { useUpdateStopFields } from './hooks/use-update-stop-fields'
import { RemoveStopButton } from './remove-stop-button'

const stopIcons: Record<TStopType, JSX.Element> = {
  Both: <ArrowRightLeft className="h-3 w-3" />,
  Drop: <ArrowDown className="h-3 w-3" />,
  Pick: <ArrowUp className="h-3 w-3" />
}

const getStopIcon = (type: TStopType) => stopIcons[type]

interface StopCardProps {
  canEditStops: boolean
  stop: IStop
}

/**
 * StopCard component with inline editing capabilities
 * Similar to the old app's inline editing pattern
 */
export const StopCard = ({ stop, canEditStops }: StopCardProps) => {
  const { editingStopId, setEditingStopId } = useStops()
  const { handleUpdateStop } = useUpdateStopDetails(stop)

  const { handleFieldUpdate: handleETAUpdate } = useUpdateStopFields('eta', stop)
  const { handleFieldUpdate: handleArrivalUpdate } = useUpdateStopFields('arrival', stop)
  const { handleFieldUpdate: handleDepartureUpdate } = useUpdateStopFields('departure', stop)

  const isEditingWarehouse = editingStopId === stop.id
  const stopCityState = [stop.warehouse?.address?.city, stop.warehouse?.address?.stateCode].filter(Boolean).join(', ')
  const stopHeading = stopCityState || `Stop ${stop.sequence}`

  const handleWarehouseEdit = () => {
    setEditingStopId(stop.id)
  }

  const handleWarehouseUpdate = (warehouse: IStopWarehouse | null) => {
    handleUpdateStop({ warehouse })
    setEditingStopId(null)
  }

  const handleWarehouseCancelEdit = () => {
    setEditingStopId(null)
  }

  const handleInstructionsUpdate = (instructions: string) => {
    handleUpdateStop({ instructions })
  }

  const handleRefNumberUpdate = (referenceNumber: string) => {
    handleUpdateStop({ referenceNumber })
  }

  const handleStopTypeUpdate = (type: TStopType) => {
    handleUpdateStop({ type })
  }

  const handleAppointmentUpdate = (appointment: {
    end: IDateTime | null
    isFirstComeFirstServe: boolean
    start: IDateTime | null
  }) => {
    handleUpdateStop({ appointment })
  }

  return (
    <Card className="relative gap-2">
      {/* Header Section */}
      <CardHeader className="space-y-2">
        <CardTitle className="text-base">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div
                className={`flex-shrink-0 p-1 ${
                  stop.type === 'Pick'
                    ? 'rounded-full bg-slate-900 text-white dark:bg-slate-100 dark:text-black'
                    : 'rounded-md bg-blue-600 text-white dark:bg-blue-600 dark:text-white'
                }`}
              >
                {getStopIcon(stop.type ?? 'Drop')}
              </div>
              <span className="truncate text-sm font-medium text-foreground">{stopHeading}</span>
            </div>
          </div>
        </CardTitle>
        <div>
          {/* Warehouse Info */}
          {canEditStops ? (
            <WarehouseInput
              warehouse={stop.warehouse}
              isEditing={isEditingWarehouse}
              onEdit={handleWarehouseEdit}
              onCancelEdit={handleWarehouseCancelEdit}
              onWarehouseUpdate={handleWarehouseUpdate}
            />
          ) : (
            <WarehouseDisplay warehouse={stop.warehouse} />
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Remove Stop Button - only show when can edit */}
        {!!canEditStops && (
          <div className="absolute top-2 right-2">
            <RemoveStopButton stopID={stop.id} />
          </div>
        )}

        {/* Instructions */}
        {canEditStops ? (
          <InstructionsInput instructions={stop.instructions || ''} onSubmit={handleInstructionsUpdate} />
        ) : (
          <InstructionsDisplay instructions={stop.instructions || ''} />
        )}
        {!canEditStops && <RefNumberDisplay referenceNumber={stop.referenceNumber || ''} />}

        {/* Stop Type and Reference Number - Editable Row */}
        {!!canEditStops && (
          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2">
            <RefNumberInput referenceNumber={stop.referenceNumber || ''} onSubmit={handleRefNumberUpdate} />
            <StopTypeInput type={stop.type} onSubmit={handleStopTypeUpdate} />
          </div>
        )}

        {/* Appointment Section */}
        {canEditStops ? (
          <AppointmentSection appointment={stop.appointment} onUpdate={handleAppointmentUpdate} />
        ) : (
          <AppointmentDisplay appointment={stop.appointment} />
        )}

        {/* ETA, Arrival, Departure Times */}
        <div className="space-y-1.5 pt-2 border-t">
          <div className="space-y-1">
            <Label className="text-xs font-medium">ETA</Label>
            <DateTimeField dateTime={stop.eta} onUpdate={handleETAUpdate} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Check In</Label>
            <DateTimeField dateTime={stop.arrivalDateTime} onUpdate={handleArrivalUpdate} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-medium">Check Out</Label>
            <DateTimeField dateTime={stop.departureDateTime} onUpdate={handleDepartureUpdate} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
